"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ArrowRightIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  KeyIcon,
  ScaleIcon,
  GiftIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const CONTENT = {
  fr: {
    h1: "Débarras d'appartement à Bruxelles",
    heroText:
      "Nous débarrassons tous types d'appartements à Bruxelles : studio, appartement familial, kot étudiant ou bien situé dans un immeuble avec syndic. L'intervention comprend le tri sur place, l'évacuation du mobilier, le rachat de ce qui a de la valeur et le nettoyage sommaire du logement vidé. Nous nous adaptons aux contraintes propres aux immeubles bruxellois — étages sans ascenseur, horaires de copropriété, accès limité — pour organiser le débarras sans perturber les autres occupants. Chaque devis est gratuit et établi sur la base de la situation réelle du bien.",
    freeQuote: "Demander un devis gratuit",
    coverage: {
      title: "Ce que couvre un débarras d'appartement",
      p1: "Un débarras d'appartement consiste à vider entièrement un logement — meubles, électroménager, vêtements, vaisselle, objets personnels restants — pour le rendre libre et propre. C'est une intervention plus rapide qu'un débarras de maison, mais souvent plus contrainte par l'accès : étage, ascenseur, couloirs étroits, règlement de copropriété. Ces paramètres influencent directement l'organisation de l'intervention.",
      p2before: "Si vous videz une maison entière plutôt qu'un appartement, notre page ",
      p2link1: "débarras de maison à Bruxelles",
      p2mid1: " détaille cette intervention. Pour une cave, un grenier ou un garage à vider isolément, voir ",
      p2link2: "débarras de cave, grenier et garage",
      p2mid2: ". Lorsque l'ensemble du bien doit être vidé dans le cadre d'une succession ou d'une vente, notre service de ",
      p2link3: "vide maison à Bruxelles",
      p2after: " couvre les 19 communes de la région.",
    },
    situations: {
      title: "Dans quels cas intervenons-nous ?",
      p: "Le débarras d'appartement répond le plus souvent à une échéance précise : fin de bail, succession, mise en vente ou en location. Voici les situations les plus fréquentes que nous rencontrons à Bruxelles.",
      items: [
        "Fin de bail à libérer avant l'état des lieux",
        "Succession portant sur un appartement",
        "Kot étudiant à vider en fin d'année académique",
        "Appartement à remettre en état avant une vente",
        "Départ en maison de repos ou en famille d'accueil",
        "Séparation nécessitant de vider un logement commun",
      ],
      pAfterBefore: "Une succession touche rarement un seul logement isolé : notre article sur la ",
      pAfterLink: "gestion d'une succession à Bruxelles",
      pAfterEnd: " explique les étapes à anticiper au-delà du seul débarras.",
    },
    steps: {
      title: "Comment se déroule l'intervention",
      items: [
        {
          title: "Premier contact et devis",
          text: "Vous décrivez la situation (surface, étage, ascenseur, contraintes d'accès) par téléphone ou via le formulaire. Un devis gratuit est établi sur cette base, complété si besoin par des photos.",
        },
        {
          title: "Tri et estimation sur place",
          text: "Avant toute évacuation, nous examinons les meubles et objets présents pour identifier ce qui peut être racheté et déduit du montant du débarras.",
        },
        {
          title: "Évacuation de l'appartement",
          text: "Le mobilier et les objets restants sont sortis, transportés et orientés vers les filières adaptées : réemploi, recyclage ou déchets ultimes selon leur nature.",
        },
        {
          title: "Remise du logement",
          text: "L'appartement est laissé balayé et débarrassé, prêt pour l'état des lieux, la mise en location ou la visite d'un acheteur.",
        },
      ],
    },
    prix: {
      title: "Prix et délai : les facteurs qui comptent",
      p: "Il n'existe pas de tarif fixe au m² ou par pièce : chaque appartement est différent, et un studio n'a rien à voir avec un trois-chambres encombré depuis des années. Nous préférons établir un devis gratuit reflétant la situation réelle plutôt qu'annoncer un prix générique.",
      items: [
        "Le volume de mobilier et d'objets à évacuer",
        "L'étage et la présence ou non d'un ascenseur",
        "Les contraintes d'accès (stationnement, largeur des couloirs, horaires de l'immeuble)",
        "La présence d'objets valorisables, déduits du devis",
        "Le degré de tri déjà réalisé avant notre passage",
      ],
      pAfterBefore: "Le détail de ces critères et de la méthode de devis est expliqué sur notre page ",
      pAfterLink: "tarifs vide maison à Bruxelles",
      pAfterEnd: ".",
    },
    tri: {
      title: "Tri, évacuation et rachat d'objets de valeur",
      p: "Avant toute évacuation, nous trions ce qui se trouve dans l'appartement. Ce tri sert à deux choses : repérer les objets réellement valorisables, et orienter le reste vers la bonne filière — réemploi, recyclage ou déchets ultimes — plutôt que tout mélanger dans une benne.",
      badge: "Rachat d'objets de valeur",
      boxText:
        "Meubles anciens, bijoux, argenterie, œuvres d'art ou collections : lorsqu'un objet mérite une estimation, nous le rachetons et déduisons sa valeur du montant du débarras. Cette évaluation se fait sur place, avant l'évacuation, jamais après.",
      cta: "En savoir plus sur le rachat d'objets",
    },
    acces: {
      title: "Accès, copropriété et logistique",
      p: "La particularité d'un appartement, par rapport à une maison, tient surtout à l'accès. Beaucoup d'immeubles bruxellois n'ont pas d'ascenseur, imposent des horaires d'utilisation des parties communes ou nécessitent l'accord du syndic pour occuper le hall ou le trottoir le temps du chargement.",
      items: [
        "Coordination avec le syndic pour les immeubles à règlement de copropriété (horaires, protection des communs).",
        "Organisation de l'accès aux clés lorsque le propriétaire ou le locataire ne peut pas être présent sur place.",
        "Adaptation à l'étage et à l'absence d'ascenseur : la manutention est prévue en conséquence dans le devis.",
        "Prise en compte du stationnement disponible pour le véhicule d'évacuation, selon la rue et le quartier.",
      ],
      pAfterBefore: "Notre article ",
      pAfterLink: "checklist avant un débarras",
      pAfterEnd:
        " liste les points à vérifier de votre côté avant notre passage, notamment lorsque l'accès dépend d'un tiers (syndic, agence, famille).",
    },
    pourquoi: {
      title: "Pourquoi choisir notre équipe pour ce débarras",
      items: [
        "Devis gratuit et sans engagement, établi avant toute intervention",
        "Estimation des objets de valeur avant évacuation, jamais après",
        "Organisation adaptée aux horaires et règlements de copropriété",
        "Un interlocuteur unique du premier appel jusqu'à la remise des clés",
      ],
      pBefore: "Vous pouvez consulter des exemples d'interventions sur notre page ",
      pLink1: "réalisations",
      pMid: " ou lire les ",
      pLink2: "avis de clients",
      pMid2:
        " avant de nous contacter. Pour un aperçu plus complet des spécificités d'un appartement bruxellois, notre guide ",
      pLink3: "débarras d'appartement à Bruxelles",
      pEnd: " complète cette page.",
    },
    faq: {
      title: "Questions fréquentes",
      items: [
        {
          q: "Combien coûte un débarras d'appartement à Bruxelles ?",
          a: "Il n'y a pas de tarif fixe : le prix dépend du volume à évacuer, de l'étage, de la présence d'un ascenseur et des objets de valeur trouvés sur place, qui sont déduits du devis. Nous établissons un devis gratuit et personnalisé après avoir pris connaissance de la situation, par téléphone ou sur place selon le cas.",
        },
        {
          q: "Combien de temps prend le débarras d'un appartement ?",
          a: "La durée dépend surtout de la surface, de la quantité de mobilier et de l'accès (étage, ascenseur, stationnement). Un studio se vide plus vite qu'un appartement familial de plusieurs pièces. Le délai précis est communiqué avec le devis, une fois la situation réelle connue.",
        },
        {
          q: "Dois-je être présent le jour du débarras ?",
          a: "Ce n'est pas obligatoire si les modalités d'accès et le périmètre de l'intervention ont été convenus à l'avance, par exemple avec un syndic, une agence ou un membre de la famille disposant des clés. Votre présence reste toutefois utile pour valider sur place ce qui doit être conservé et éviter tout malentendu sur le périmètre exact de l'intervention.",
        },
        {
          q: "Comment gérez-vous l'accès dans un immeuble avec syndic ?",
          a: "Nous nous coordonnons avec le syndic ou le gestionnaire de l'immeuble pour respecter le règlement de copropriété : horaires autorisés, protection des parties communes, usage de l'ascenseur ou du monte-charge. Ces éléments sont à nous signaler dès la demande de devis afin d'être anticipés et de ne pas retarder l'intervention le jour venu.",
        },
        {
          q: "Que faites-vous des meubles ou objets qui ont de la valeur ?",
          a: "Avant d'évacuer quoi que ce soit, nous identifions les meubles anciens, bijoux, argenterie, tableaux ou objets qui méritent une estimation. Lorsqu'un rachat est possible, sa valeur est déduite du montant du débarras plutôt que d'être jetée avec le reste. Cette étape se fait toujours sur place, avant le début de l'évacuation.",
        },
        {
          q: "Comment sont évacués les meubles et déchets ?",
          a: "Après le tri, les objets récupérables suivent une filière de réemploi ou de recyclage, et le reste est évacué comme déchet ultime. L'appartement est rendu vide et balayé, sans que vous ayez à gérer vous-même le transport, le chargement ou l'accès à un container ou à un centre de tri.",
        },
        {
          q: "Faut-il préparer l'appartement avant votre passage ?",
          a: "Aucune préparation n'est indispensable pour que nous intervenions. Il est toutefois utile de mettre de côté les objets personnels ou documents à conserver, et de nous signaler à l'avance tout élément fragile ou de valeur pour qu'il soit traité en priorité plutôt que mélangé au reste du mobilier.",
        },
        {
          q: "Intervenez-vous pour un studio ou un kot étudiant ?",
          a: "Oui, ce sont des interventions fréquentes à Bruxelles, notamment en fin de bail ou d'année académique, quand un logement doit être libéré rapidement pour l'état des lieux. Le principe reste le même que pour un appartement plus grand : tri, estimation des objets de valeur, puis évacuation dans un délai adapté à la contrainte de restitution du bien.",
        },
      ],
    },
    ctaFinal: {
      title: "Un devis gratuit pour votre appartement à Bruxelles",
      p: "Décrivez-nous votre situation — surface, étage, échéance — et recevez un devis gratuit et sans engagement, quel que soit l'accès à votre immeuble.",
    },
  },
  nl: {
    h1: "Appartement ontruimen in Brussel",
    heroText:
      "Wij ontruimen alle soorten appartementen in Brussel: studio's, gezinsappartementen, studentenkoten of woningen in een gebouw met syndicus. De interventie omvat de sortering ter plaatse, de afvoer van het meubilair, de inkoop van waardevolle voorwerpen en een summiere schoonmaak van de leeggemaakte woning. Wij passen ons aan de specifieke beperkingen van Brusselse gebouwen aan — verdiepingen zonder lift, reglementen van de mede-eigendom, beperkte toegang — om de ontruiming te organiseren zonder de andere bewoners te storen. Elke offerte is gratis en wordt opgesteld op basis van de werkelijke situatie van het pand.",
    freeQuote: "Vraag een gratis offerte aan",
    coverage: {
      title: "Wat een appartement ontruiming omvat",
      p1: "Een appartement ontruimen betekent een woning volledig leegmaken — meubels, huishoudapparaten, kleding, servies, achtergebleven persoonlijke voorwerpen — om ze vrij en proper op te leveren. Dit is een snellere interventie dan een huis ontruimen, maar vaak sterker beperkt door de toegang: verdieping, lift, smalle gangen, reglement van mede-eigendom. Deze factoren bepalen rechtstreeks de organisatie van de interventie.",
      p2before: "Als u eerder een volledig huis moet leegmaken dan een appartement, beschrijft onze pagina ",
      p2link1: "huis ontruimen in Brussel",
      p2mid1: " deze interventie in detail. Voor een kelder, zolder of garage die afzonderlijk moet worden leeggemaakt, zie ",
      p2link2: "kelder, zolder en garage ontruimen",
      p2mid2: ". Wanneer het volledige pand moet worden leeggemaakt in het kader van een erfenis of verkoop, dekt onze dienst ",
      p2link3: "huis leegmaken in Brussel",
      p2after: " de 19 gemeenten van het gewest.",
    },
    situations: {
      title: "In welke gevallen komen wij tussen?",
      p: "Een appartement ontruimen is meestal gekoppeld aan een concrete deadline: einde huurcontract, erfenis, verkoop of verhuur. Hieronder de meest voorkomende situaties die wij in Brussel tegenkomen.",
      items: [
        "Einde huurcontract, woning moet vrij zijn voor de plaatsbeschrijving",
        "Erfenis met betrekking tot een appartement",
        "Studentenkot leegmaken aan het einde van het academiejaar",
        "Appartement in orde brengen vóór een verkoop",
        "Vertrek naar een rusthuis of pleeggezin",
        "Scheiding waarbij een gemeenschappelijke woning moet worden leeggemaakt",
      ],
      pAfterBefore: "Een erfenis betreft zelden slechts één geïsoleerde woning: ons artikel over het ",
      pAfterLink: "beheer van een erfenis in Brussel",
      pAfterEnd: " legt de stappen uit die u naast de ontruiming zelf moet plannen.",
    },
    steps: {
      title: "Hoe verloopt de interventie",
      items: [
        {
          title: "Eerste contact en offerte",
          text: "U beschrijft de situatie (oppervlakte, verdieping, lift, toegangsbeperkingen) telefonisch of via het formulier. Op basis hiervan stellen wij een gratis offerte op, indien nodig aangevuld met foto's.",
        },
        {
          title: "Sortering en schatting ter plaatse",
          text: "Vóór elke afvoer bekijken wij de aanwezige meubels en voorwerpen om te bepalen wat kan worden ingekocht en van het ontruimingsbedrag kan worden afgetrokken.",
        },
        {
          title: "Ontruiming van het appartement",
          text: "Het meubilair en de overige voorwerpen worden buitengebracht, vervoerd en naar de gepaste kanalen geleid: hergebruik, recyclage of restafval, naargelang hun aard.",
        },
        {
          title: "Oplevering van de woning",
          text: "Het appartement wordt geveegd en leeg achtergelaten, klaar voor de plaatsbeschrijving, de verhuur of het bezoek van een koper.",
        },
      ],
    },
    prix: {
      title: "Prijs en termijn: de factoren die tellen",
      p: "Er bestaat geen vast tarief per m² of per kamer: elk appartement is anders, en een studio heeft niets te maken met een driekamerappartement dat al jaren vol staat. Wij geven de voorkeur aan een gratis offerte die de werkelijke situatie weerspiegelt, boven een algemene prijsopgave.",
      items: [
        "Het volume aan meubilair en voorwerpen dat moet worden afgevoerd",
        "De verdieping en de al dan niet aanwezigheid van een lift",
        "De toegangsbeperkingen (parkeren, breedte van de gangen, openingstijden van het gebouw)",
        "De aanwezigheid van waardevolle voorwerpen, afgetrokken van de offerte",
        "De mate waarin al gesorteerd werd vóór onze komst",
      ],
      pAfterBefore: "De details van deze criteria en van onze offertemethode worden toegelicht op onze pagina ",
      pAfterLink: "tarieven huis leegmaken in Brussel",
      pAfterEnd: ".",
    },
    tri: {
      title: "Sortering, afvoer en inkoop van waardevolle voorwerpen",
      p: "Vóór elke afvoer sorteren wij wat zich in het appartement bevindt. Deze sortering dient twee doelen: de werkelijk waardevolle voorwerpen opsporen, en de rest naar het juiste kanaal leiden — hergebruik, recyclage of restafval — in plaats van alles in één container te mengen.",
      badge: "Inkoop van waardevolle voorwerpen",
      boxText:
        "Antieke meubels, juwelen, zilverwerk, kunstwerken of verzamelingen: wanneer een voorwerp een schatting waard is, kopen wij het aan en trekken wij de waarde af van het ontruimingsbedrag. Deze evaluatie gebeurt ter plaatse, vóór de afvoer, nooit erna.",
      cta: "Meer weten over de inkoop van voorwerpen",
    },
    acces: {
      title: "Toegang, mede-eigendom en logistiek",
      p: "Het bijzondere aan een appartement, in vergelijking met een huis, zit vooral in de toegang. Veel Brusselse gebouwen hebben geen lift, leggen openingstijden op voor het gebruik van de gemeenschappelijke ruimtes, of vereisen de toestemming van de syndicus om de inkomhal of de stoep tijdens het laden te gebruiken.",
      items: [
        "Coördinatie met de syndicus voor gebouwen met een reglement van mede-eigendom (openingstijden, bescherming van de gemeenschappelijke delen).",
        "Organisatie van de toegang tot de sleutels wanneer de eigenaar of de huurder niet aanwezig kan zijn.",
        "Aanpassing aan de verdieping en het ontbreken van een lift: het transport wordt hierop afgestemd in de offerte.",
        "Rekening houden met de beschikbare parkeermogelijkheden voor het afvoervoertuig, afhankelijk van de straat en de buurt.",
      ],
      pAfterBefore: "Ons artikel ",
      pAfterLink: "checklist vóór een ontruiming",
      pAfterEnd:
        " somt de punten op die u zelf moet controleren vóór onze komst, vooral wanneer de toegang afhangt van een derde partij (syndicus, agentschap, familie).",
    },
    pourquoi: {
      title: "Waarom kiezen voor ons team voor deze ontruiming",
      items: [
        "Gratis en vrijblijvende offerte, opgesteld vóór elke interventie",
        "Schatting van waardevolle voorwerpen vóór de afvoer, nooit erna",
        "Organisatie afgestemd op de openingstijden en reglementen van de mede-eigendom",
        "Eén vast aanspreekpunt van het eerste telefoontje tot de sleuteloverdracht",
      ],
      pBefore: "U kunt voorbeelden van onze interventies bekijken op onze pagina ",
      pLink1: "realisaties",
      pMid: " of de ",
      pLink2: "beoordelingen van klanten",
      pMid2:
        " lezen voordat u contact met ons opneemt. Voor een uitgebreider overzicht van de specifieke kenmerken van een Brussels appartement vult onze gids ",
      pLink3: "appartement ontruimen in Brussel",
      pEnd: " deze pagina aan.",
    },
    faq: {
      title: "Veelgestelde vragen",
      items: [
        {
          q: "Hoeveel kost een appartement ontruiming in Brussel?",
          a: "Er is geen vast tarief: de prijs hangt af van het volume dat moet worden afgevoerd, de verdieping, de aanwezigheid van een lift en de waardevolle voorwerpen die ter plaatse worden gevonden, die van de offerte worden afgetrokken. Wij stellen een gratis en persoonlijke offerte op nadat we de situatie hebben besproken, telefonisch of ter plaatse naargelang het geval.",
        },
        {
          q: "Hoe lang duurt de ontruiming van een appartement?",
          a: "De duur hangt vooral af van de oppervlakte, de hoeveelheid meubilair en de toegang (verdieping, lift, parkeren). Een studio wordt sneller leeggemaakt dan een gezinsappartement met meerdere kamers. De precieze termijn wordt meegedeeld met de offerte, zodra de werkelijke situatie gekend is.",
        },
        {
          q: "Moet ik aanwezig zijn op de dag van de ontruiming?",
          a: "Dat is niet verplicht als de toegangsmodaliteiten en de omvang van de interventie vooraf zijn afgesproken, bijvoorbeeld met een syndicus, een agentschap of een familielid met sleutels. Uw aanwezigheid blijft echter nuttig om ter plaatse te bevestigen wat bewaard moet blijven en om elk misverstand over de exacte omvang van de interventie te vermijden.",
        },
        {
          q: "Hoe regelt u de toegang in een gebouw met een syndicus?",
          a: "Wij stemmen af met de syndicus of de beheerder van het gebouw om het reglement van mede-eigendom na te leven: toegestane uren, bescherming van de gemeenschappelijke delen, gebruik van de lift of de goederenlift. Deze elementen moeten al bij de offerteaanvraag worden gemeld, zodat ze kunnen worden ingepland en de interventie op de dag zelf niet vertraagd wordt.",
        },
        {
          q: "Wat doet u met meubels of voorwerpen die waarde hebben?",
          a: "Vóór we iets afvoeren, identificeren wij antieke meubels, juwelen, zilverwerk, schilderijen of voorwerpen die een schatting waard zijn. Wanneer een inkoop mogelijk is, wordt de waarde ervan afgetrokken van het ontruimingsbedrag in plaats van met de rest te worden weggegooid. Deze stap gebeurt altijd ter plaatse, vóór het begin van de afvoer.",
        },
        {
          q: "Hoe worden de meubels en het afval afgevoerd?",
          a: "Na de sortering volgen de recupereerbare voorwerpen een traject van hergebruik of recyclage, en wordt de rest als restafval afgevoerd. Het appartement wordt leeg en geveegd opgeleverd, zonder dat u zelf het transport, het laden of de toegang tot een container of sorteercentrum hoeft te regelen.",
        },
        {
          q: "Moet ik het appartement voorbereiden vóór onze komst?",
          a: "Geen enkele voorbereiding is noodzakelijk om te kunnen tussenkomen. Het is echter nuttig om persoonlijke voorwerpen of documenten die u wilt bewaren opzij te leggen, en ons vooraf te informeren over breekbare of waardevolle elementen, zodat deze prioritair behandeld worden in plaats van vermengd te worden met de rest van het meubilair.",
        },
        {
          q: "Komt u ook tussen voor een studio of studentenkot?",
          a: "Ja, dit zijn veelvoorkomende interventies in Brussel, vooral bij het einde van een huurcontract of academiejaar, wanneer een woning snel moet worden vrijgemaakt voor de plaatsbeschrijving. Het principe blijft hetzelfde als bij een groter appartement: sortering, schatting van waardevolle voorwerpen, en vervolgens afvoer binnen een termijn die is afgestemd op de verplichting om het pand terug te geven.",
        },
      ],
    },
    ctaFinal: {
      title: "Een gratis offerte voor uw appartement in Brussel",
      p: "Beschrijf ons uw situatie — oppervlakte, verdieping, deadline — en ontvang een gratis en vrijblijvende offerte, ongeacht de toegang tot uw gebouw.",
    },
  },
  en: {
    h1: "Apartment Clearance in Brussels",
    heroText:
      "We clear all types of apartments in Brussels: studios, family apartments, student rooms, or properties in a building managed by a syndic. The service includes on-site sorting, removal of furniture, buyback of items with value, and a basic clean of the cleared home. We adapt to the specific constraints of Brussels buildings — floors without a lift, co-ownership schedules, limited access — to organise the clearance without disturbing other residents. Every quote is free and based on the actual condition of the property.",
    freeQuote: "Request a free quote",
    coverage: {
      title: "What apartment clearance covers",
      p1: "Apartment clearance means completely emptying a home — furniture, appliances, clothing, tableware, remaining personal items — to leave it vacant and clean. It is a faster service than clearing a whole house, but often more constrained by access: floor, lift, narrow corridors, co-ownership rules. These factors directly shape how the intervention is organised.",
      p2before: "If you're clearing an entire house rather than an apartment, our ",
      p2link1: "house clearance in Brussels",
      p2mid1: " page covers that service in detail. For a cellar, attic or garage to be cleared on its own, see ",
      p2link2: "cellar, attic and garage clearance",
      p2mid2: ". When the whole property needs to be cleared as part of an estate or a sale, our ",
      p2link3: "house clearance service in Brussels",
      p2after: " covers all 19 communes of the region.",
    },
    situations: {
      title: "In which situations do we intervene?",
      p: "Apartment clearance is most often tied to a specific deadline: end of lease, estate settlement, sale or rental. Here are the most common situations we encounter in Brussels.",
      items: [
        "End of lease, property to be vacated before the check-out inspection",
        "Estate settlement involving an apartment",
        "Student room to be cleared at the end of the academic year",
        "Apartment to be prepared before a sale",
        "Move to a care home or foster family",
        "Separation requiring a shared home to be cleared",
      ],
      pAfterBefore: "An estate settlement rarely involves just one isolated property: our article on ",
      pAfterLink: "managing an estate in Brussels",
      pAfterEnd: " explains the steps to anticipate beyond the clearance itself.",
    },
    steps: {
      title: "How the service unfolds",
      items: [
        {
          title: "First contact and quote",
          text: "You describe the situation (surface area, floor, lift, access constraints) by phone or via the form. A free quote is drawn up on this basis, supplemented with photos if needed.",
        },
        {
          title: "On-site sorting and appraisal",
          text: "Before any removal, we examine the furniture and items present to identify what can be bought back and deducted from the clearance price.",
        },
        {
          title: "Clearing the apartment",
          text: "The furniture and remaining items are taken out, transported and directed to the appropriate channels: reuse, recycling or final waste disposal, depending on their nature.",
        },
        {
          title: "Handover of the property",
          text: "The apartment is left swept and cleared, ready for the check-out inspection, rental or a buyer's visit.",
        },
      ],
    },
    prix: {
      title: "Price and timeframe: the factors that matter",
      p: "There is no fixed rate per square metre or per room: every apartment is different, and a studio has nothing in common with a three-bedroom flat cluttered for years. We prefer to draw up a free quote reflecting the actual situation rather than quoting a generic price.",
      items: [
        "The volume of furniture and items to be removed",
        "The floor and whether or not a lift is present",
        "Access constraints (parking, corridor width, building opening hours)",
        "The presence of valuable items, deducted from the quote",
        "How much sorting has already been done before our visit",
      ],
      pAfterBefore: "The details of these criteria and our quoting method are explained on our ",
      pAfterLink: "house clearance prices in Brussels",
      pAfterEnd: " page.",
    },
    tri: {
      title: "Sorting, removal and buyback of valuable items",
      p: "Before any removal, we sort through everything in the apartment. This sorting serves two purposes: identifying genuinely valuable items, and directing the rest to the right channel — reuse, recycling or final waste — rather than mixing everything into one skip.",
      badge: "Buyback of valuable items",
      boxText:
        "Antique furniture, jewellery, silverware, artworks or collections: when an item deserves an appraisal, we buy it back and deduct its value from the clearance price. This assessment is done on site, before removal, never after.",
      cta: "Learn more about item buyback",
    },
    acces: {
      title: "Access, co-ownership and logistics",
      p: "What sets an apartment apart from a house is mainly access. Many Brussels buildings have no lift, impose set hours for using common areas, or require the syndic's approval to use the hallway or pavement while loading.",
      items: [
        "Coordination with the syndic for buildings under co-ownership rules (hours, protection of common areas).",
        "Arranging key access when the owner or tenant cannot be present on site.",
        "Adapting to the floor and the absence of a lift: handling is planned accordingly in the quote.",
        "Taking available parking into account for the removal vehicle, depending on the street and neighbourhood.",
      ],
      pAfterBefore: "Our article ",
      pAfterLink: "checklist before a clearance",
      pAfterEnd:
        " lists the points to check on your side before our visit, especially when access depends on a third party (syndic, agency, family).",
    },
    pourquoi: {
      title: "Why choose our team for this clearance",
      items: [
        "Free, no-obligation quote, drawn up before any intervention",
        "Appraisal of valuable items before removal, never after",
        "Organisation adapted to co-ownership hours and rules",
        "A single point of contact from the first call to the handover of keys",
      ],
      pBefore: "You can view examples of past interventions on our ",
      pLink1: "our work",
      pMid: " page or read ",
      pLink2: "customer reviews",
      pMid2:
        " before contacting us. For a fuller picture of the specifics of a Brussels apartment, our guide ",
      pLink3: "apartment clearance in Brussels",
      pEnd: " complements this page.",
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          q: "How much does apartment clearance cost in Brussels?",
          a: "There is no fixed rate: the price depends on the volume to be removed, the floor, whether a lift is present, and any valuable items found on site, which are deducted from the quote. We draw up a free, personalised quote after learning about the situation, by phone or on site depending on the case.",
        },
        {
          q: "How long does clearing an apartment take?",
          a: "The duration mainly depends on the surface area, the amount of furniture, and access (floor, lift, parking). A studio empties faster than a family apartment with several rooms. The precise timeframe is communicated along with the quote, once the actual situation is known.",
        },
        {
          q: "Do I need to be present on the day of the clearance?",
          a: "It's not mandatory if the access arrangements and the scope of the intervention have been agreed in advance, for example with a syndic, an agency, or a family member holding the keys. Your presence is nonetheless useful to confirm on site what should be kept and to avoid any misunderstanding about the exact scope of the intervention.",
        },
        {
          q: "How do you handle access in a building with a syndic?",
          a: "We coordinate with the syndic or building manager to comply with the co-ownership rules: permitted hours, protection of common areas, use of the lift or goods lift. Please flag these details when requesting your quote so they can be anticipated and the intervention isn't delayed on the day.",
        },
        {
          q: "What do you do with furniture or items that have value?",
          a: "Before removing anything, we identify antique furniture, jewellery, silverware, paintings or items worth appraising. When a buyback is possible, its value is deducted from the clearance price rather than thrown out with the rest. This step always happens on site, before removal begins.",
        },
        {
          q: "How are furniture and waste removed?",
          a: "After sorting, recoverable items are directed to reuse or recycling, and the rest is disposed of as final waste. The apartment is left empty and swept, without you having to arrange transport, loading, or access to a container or sorting centre yourself.",
        },
        {
          q: "Do I need to prepare the apartment before your visit?",
          a: "No preparation is essential for us to intervene. However, it's useful to set aside personal items or documents you wish to keep, and to tell us in advance about any fragile or valuable items so they are handled as a priority rather than mixed in with the rest of the furniture.",
        },
        {
          q: "Do you handle a studio or a student room?",
          a: "Yes, these are frequent interventions in Brussels, particularly at the end of a lease or academic year, when a property must be vacated quickly for the check-out inspection. The principle is the same as for a larger apartment: sorting, appraisal of valuable items, then removal within a timeframe suited to the deadline for returning the property.",
        },
      ],
    },
    ctaFinal: {
      title: "A free quote for your apartment in Brussels",
      p: "Describe your situation to us — surface area, floor, deadline — and receive a free, no-obligation quote, whatever the access to your building.",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 480px at 15% -10%, rgba(27,75,51,0.06), transparent 55%)" }}
        />
        <div className="relative mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-28">
          <h1 className="max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* CE QUE COUVRE LE SERVICE */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.coverage.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.coverage.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.coverage.p2before}
            <Link href="/debarras-maison" className="font-semibold text-accent hover:text-accent-dark">
              {c.coverage.p2link1}
            </Link>
            {c.coverage.p2mid1}
            <Link href="/debarras-cave-grenier-garage" className="font-semibold text-accent hover:text-accent-dark">
              {c.coverage.p2link2}
            </Link>
            {c.coverage.p2mid2}
            <Link href="/vide-maison" className="font-semibold text-accent hover:text-accent-dark">
              {c.coverage.p2link3}
            </Link>
            {c.coverage.p2after}
          </p>
        </div>
      </section>

      {/* SITUATIONS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.situations.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.situations.p}
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.situations.items.map((s) => (
              <li key={s} className="flex items-start gap-2 rounded-2xl border border-sand-line bg-white p-4 text-sm font-semibold text-ink">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                {s}
              </li>
            ))}
          </ul>
          <p className="mt-6 leading-relaxed text-neutral-700">
            {c.situations.pAfterBefore}
            <Link href="/blog/vide-maison-succession-bruxelles" className="font-semibold text-accent hover:text-accent-dark">
              {c.situations.pAfterLink}
            </Link>
            {c.situations.pAfterEnd}
          </p>
        </div>
      </section>

      {/* DEROULEMENT */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.steps.title}
          </h2>
          <ol className="mt-8 space-y-4">
            {c.steps.items.map((step, i) => (
              <li key={step.title} className="flex gap-4 rounded-2xl border border-sand-line bg-sand p-5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-900 text-sm font-bold text-white">
                  {i + 1}
                </span>
                <div>
                  <p className="font-bold text-ink">{step.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PRIX ET DELAI */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.prix.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.prix.p}
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.prix.items.map((f) => (
              <div key={f} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <span className="text-sm font-semibold text-ink">{f}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 leading-relaxed text-neutral-700">
            {c.prix.pAfterBefore}
            <Link href="/tarifs" className="font-semibold text-accent hover:text-accent-dark">
              {c.prix.pAfterLink}
            </Link>
            {c.prix.pAfterEnd}
          </p>
        </div>
      </section>

      {/* TRI, EVACUATION, RACHAT */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.tri.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.tri.p}
          </p>

          <div className="mt-8 rounded-[2rem] border border-sage/25 bg-sage/5 p-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-sage">
              <GiftIcon className="h-4 w-4" />
              {c.tri.badge}
            </div>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.tri.boxText}
            </p>
            <div className="mt-4">
              <Link href="/rachat-objets" className="inline-flex items-center gap-2 font-bold text-sage hover:text-ink">
                {c.tri.cta}
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ACCES ET LOGISTIQUE */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.acces.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.acces.p}
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
              <BuildingOffice2Icon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <span className="text-sm leading-relaxed text-neutral-700">
                {c.acces.items[0]}
              </span>
            </div>
            <div className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
              <KeyIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <span className="text-sm leading-relaxed text-neutral-700">
                {c.acces.items[1]}
              </span>
            </div>
            <div className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
              <ScaleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <span className="text-sm leading-relaxed text-neutral-700">
                {c.acces.items[2]}
              </span>
            </div>
            <div className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <span className="text-sm leading-relaxed text-neutral-700">
                {c.acces.items[3]}
              </span>
            </div>
          </div>
          <p className="mt-6 leading-relaxed text-neutral-700">
            {c.acces.pAfterBefore}
            <Link href="/blog/checklist-avant-vide-maison" className="font-semibold text-accent hover:text-accent-dark">
              {c.acces.pAfterLink}
            </Link>
            {c.acces.pAfterEnd}
          </p>
        </div>
      </section>

      {/* POURQUOI NOUS CHOISIR */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.pourquoi.title}
          </h2>
          <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.pourquoi.items.map((r) => (
              <li key={r} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand p-5">
                <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <span className="text-sm font-semibold text-ink">{r}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 leading-relaxed text-neutral-700">
            {c.pourquoi.pBefore}
            <Link href="/realisations" className="font-semibold text-accent hover:text-accent-dark">
              {c.pourquoi.pLink1}
            </Link>
            {c.pourquoi.pMid}
            <Link href="/avis" className="font-semibold text-accent hover:text-accent-dark">
              {c.pourquoi.pLink2}
            </Link>
            {c.pourquoi.pMid2}
            <Link href="/blog/debarras-appartement-bruxelles-guide" className="font-semibold text-accent hover:text-accent-dark">
              {c.pourquoi.pLink3}
            </Link>
            {c.pourquoi.pEnd}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">{c.faq.title}</h2>
          <div className="mt-6 space-y-4">
            {c.faq.items.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-white p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1000px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.ctaFinal.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-neutral-700">
            {c.ctaFinal.p}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
